/**
 * ArchWallCollisionBuilder
 *
 * Generates curved collision meshes for arch/Quonset-style hangars.
 * Produces:
 *   1. Arch shell surface — curved outer surface extruded along Z
 *   2. Front end wall — arch-shaped ShapeGeometry with door cutouts
 *   3. Back end wall — same as front, with back door config
 *
 * Returns UnitWallCollisionResult so the rest of the collision pipeline
 * (serialization, worker, BVH detection) is unchanged.
 */

import * as THREE from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import { FEET_TO_METERS } from '@/constants/units'
import { ENABLE_COLLISION_DEBUG, DEBUG_WALL_COLLISIONS } from '@/constants/collision'
import { generateArchProfile } from '@/lib/templates/arch-hangar/calculations'
import type { DoorSpec } from '@/lib/templates/types'
import type { UnitCollisionConfig, ArchCollisionConfig } from '@/types/collision'
import type { UnitWallCollisionResult } from './UnitWallCollisionBuilder'

function debugLog(message: string, data?: unknown): void {
  if (!ENABLE_COLLISION_DEBUG || !DEBUG_WALL_COLLISIONS) return
  if (data) {
    console.log(`🏗️ [ARCH WALL] ${message}`, data)
  } else {
    console.log(`🏗️ [ARCH WALL] ${message}`)
  }
}

/**
 * Create arch-specific collision meshes for a unit with archConfig.
 * Generates curved shell + arch-shaped end walls instead of flat boxes.
 */
export function createArchCollisionMeshes(config: UnitCollisionConfig): UnitWallCollisionResult {
  const { unitId, areaId, position, rotation, archConfig } = config

  if (!archConfig) {
    throw new Error(`createArchCollisionMeshes called without archConfig for unit ${unitId}`)
  }

  const obbs: OBB[] = []
  const meshes: THREE.Mesh[] = []
  const edgeSides: Array<'front' | 'back' | 'left' | 'right'> = []
  const isDoorFlags: boolean[] = []

  const unitPos = new THREE.Vector3(position[0], position[1], position[2])
  const rotationRad = (rotation * Math.PI) / 180

  debugLog(`Creating arch collision for unit ${unitId}`, {
    span: archConfig.span_ft,
    length: archConfig.length_ft,
    crown: archConfig.crown_height_ft,
    knee: archConfig.knee_wall_height_ft,
    profile: archConfig.arch_profile,
    segments: archConfig.arch_segments,
  })

  // 1. Arch shell surface
  const shellGeometry = buildArchShellCollisionGeometry(archConfig)
  addCollisionMesh(
    shellGeometry, unitPos, rotationRad, `arch-shell-${unitId}`,
    obbs, meshes, edgeSides, isDoorFlags, 'left', false
  )

  // 2. Front end wall (with door cutouts)
  const frontGeometry = buildArchEndWallGeometry(archConfig, 'front')
  if (frontGeometry) {
    addCollisionMesh(
      frontGeometry, unitPos, rotationRad, `arch-front-${unitId}`,
      obbs, meshes, edgeSides, isDoorFlags, 'front', false
    )
  }

  // 3. Front door collision meshes
  if (archConfig.frontDoors && archConfig.frontDoors.length > 0) {
    for (const door of archConfig.frontDoors) {
      const doorGeometry = buildArchDoorGeometry(archConfig, 'front', door)
      if (doorGeometry) {
        addCollisionMesh(
          doorGeometry, unitPos, rotationRad, `arch-front-door-${unitId}`,
          obbs, meshes, edgeSides, isDoorFlags, 'front', true
        )
      }
    }
  }

  // 4. Back end wall (with door cutouts)
  const backGeometry = buildArchEndWallGeometry(archConfig, 'back')
  if (backGeometry) {
    addCollisionMesh(
      backGeometry, unitPos, rotationRad, `arch-back-${unitId}`,
      obbs, meshes, edgeSides, isDoorFlags, 'back', false
    )
  }

  // 5. Back door collision meshes
  if (archConfig.backDoors && archConfig.backDoors.length > 0) {
    for (const door of archConfig.backDoors) {
      const doorGeometry = buildArchDoorGeometry(archConfig, 'back', door)
      if (doorGeometry) {
        addCollisionMesh(
          doorGeometry, unitPos, rotationRad, `arch-back-door-${unitId}`,
          obbs, meshes, edgeSides, isDoorFlags, 'back', true
        )
      }
    }
  }

  debugLog(`Unit ${unitId}: Created ${meshes.length} arch collision meshes`)

  return { obbs, meshes, unitId, areaId, edgeSides, isDoorFlags }
}

/**
 * Build the outer-surface-only collision geometry for the arch shell.
 * Extrudes the arch profile along Z from -length/2 to +length/2.
 * Single-sided mesh (outer surface only) — sufficient for BVH intersection.
 */
function buildArchShellCollisionGeometry(arch: ArchCollisionConfig): THREE.BufferGeometry {
  const profile = generateArchProfile(
    arch.span_ft,
    arch.crown_height_ft,
    arch.knee_wall_height_ft,
    arch.arch_profile,
    arch.arch_segments
  )

  const halfLength = arch.length_ft / 2
  const numPoints = profile.length

  // 2 z-slices × numPoints vertices
  const positions = new Float32Array(numPoints * 2 * 3)
  const indices: number[] = []

  // Front slice (z = +halfLength)
  for (let i = 0; i < numPoints; i++) {
    const vi = i * 3
    positions[vi] = profile[i].x * FEET_TO_METERS
    positions[vi + 1] = profile[i].y * FEET_TO_METERS
    positions[vi + 2] = halfLength * FEET_TO_METERS
  }

  // Back slice (z = -halfLength)
  for (let i = 0; i < numPoints; i++) {
    const vi = (numPoints + i) * 3
    positions[vi] = profile[i].x * FEET_TO_METERS
    positions[vi + 1] = profile[i].y * FEET_TO_METERS
    positions[vi + 2] = -halfLength * FEET_TO_METERS
  }

  const front = 0
  const back = numPoints

  // Create quads between consecutive profile points across z-slices
  for (let i = 0; i < numPoints - 1; i++) {
    // CCW winding for outward-facing normal
    indices.push(front + i, back + i, back + i + 1)
    indices.push(front + i, back + i + 1, front + i + 1)
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setIndex(indices)

  return geometry
}

/**
 * Build an arch-shaped end wall collision geometry with door cutouts.
 * Uses THREE.Shape + holes for door openings, same approach as ArchRenderer.
 */
function buildArchEndWallGeometry(
  arch: ArchCollisionConfig,
  side: 'front' | 'back'
): THREE.BufferGeometry | null {
  const doors = side === 'front' ? arch.frontDoors : arch.backDoors

  const profile = generateArchProfile(
    arch.span_ft,
    arch.crown_height_ft,
    arch.knee_wall_height_ft,
    arch.arch_profile,
    arch.arch_segments
  )

  // Create arch-shaped outline
  const shape = new THREE.Shape()
  shape.moveTo(profile[0].x * FEET_TO_METERS, profile[0].y * FEET_TO_METERS)
  for (let i = 1; i < profile.length; i++) {
    shape.lineTo(profile[i].x * FEET_TO_METERS, profile[i].y * FEET_TO_METERS)
  }
  shape.closePath()

  // Cut door holes
  if (doors && doors.length > 0) {
    for (const door of doors) {
      const doorLeft = (door.offset_ft - arch.span_ft / 2) * FEET_TO_METERS
      const doorRight = doorLeft + door.width_ft * FEET_TO_METERS
      const doorTop = door.height_ft * FEET_TO_METERS

      const hole = new THREE.Path()
      hole.moveTo(doorLeft, 0)
      hole.lineTo(doorRight, 0)
      hole.lineTo(doorRight, doorTop)
      hole.lineTo(doorLeft, doorTop)
      hole.closePath()
      shape.holes.push(hole)
    }
  }

  const shapeGeometry = new THREE.ShapeGeometry(shape)

  // Position the end wall at the correct z offset
  const halfLength = (arch.length_ft / 2) * FEET_TO_METERS
  const zOffset = side === 'front' ? halfLength : -halfLength

  // Apply z-position transform
  const transform = new THREE.Matrix4().makeTranslation(0, 0, zOffset)

  // Back wall faces opposite direction (rotate 180° around Y)
  if (side === 'back') {
    transform.premultiply(new THREE.Matrix4().makeRotationY(Math.PI))
  }

  shapeGeometry.applyMatrix4(transform)

  return shapeGeometry
}

/**
 * Build a door collision mesh (rectangular box at door location on end wall).
 */
function buildArchDoorGeometry(
  arch: ArchCollisionConfig,
  side: 'front' | 'back',
  door: DoorSpec
): THREE.BufferGeometry | null {
  const doorWidth = door.width_ft * FEET_TO_METERS
  const doorHeight = door.height_ft * FEET_TO_METERS
  const thickness = arch.wall_thickness_ft * FEET_TO_METERS

  const geometry = new THREE.BoxGeometry(doorWidth, doorHeight, thickness)

  const doorCenterX = (door.offset_ft + door.width_ft / 2 - arch.span_ft / 2) * FEET_TO_METERS
  const doorCenterY = doorHeight / 2
  const halfLength = (arch.length_ft / 2) * FEET_TO_METERS
  const zPos = side === 'front' ? halfLength : -halfLength

  const transform = new THREE.Matrix4().makeTranslation(doorCenterX, doorCenterY, zPos)
  geometry.applyMatrix4(transform)

  return geometry
}

/**
 * Helper: bake transform, compute BVH, create OBB, push into result arrays.
 */
function addCollisionMesh(
  geometry: THREE.BufferGeometry,
  unitPos: THREE.Vector3,
  rotationRad: number,
  name: string,
  obbs: OBB[],
  meshes: THREE.Mesh[],
  edgeSides: Array<'front' | 'back' | 'left' | 'right'>,
  isDoorFlags: boolean[],
  side: 'front' | 'back' | 'left' | 'right',
  isDoor: boolean
): void {
  // Build world transform: rotate around unit center, then translate to unit position
  const transform = new THREE.Matrix4()
  if (rotationRad !== 0) {
    transform.makeRotationY(rotationRad)
  }
  transform.setPosition(
    unitPos.x + (rotationRad !== 0 ? 0 : 0), // position set after rotation
    unitPos.y,
    unitPos.z
  )

  // For rotation: we need to rotate geometry around origin first, then translate
  if (rotationRad !== 0) {
    const rotMatrix = new THREE.Matrix4().makeRotationY(rotationRad)
    geometry.applyMatrix4(rotMatrix)
  }
  // Translate to unit world position
  const translateMatrix = new THREE.Matrix4().makeTranslation(unitPos.x, unitPos.y, unitPos.z)
  geometry.applyMatrix4(translateMatrix)

  // Compute BVH for narrow-phase
  geometry.computeBoundsTree()

  const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ visible: false }))
  mesh.name = name

  meshes.push(mesh)
  edgeSides.push(side)
  isDoorFlags.push(isDoor)

  // Create OBB for broad-phase
  const positionAttribute = geometry.attributes.position as THREE.BufferAttribute
  const box = new THREE.Box3().setFromBufferAttribute(positionAttribute)
  const obb = new OBB().fromBox3(box)
  obbs.push(obb)
}
